/**
 * Delivery Scheduler
 * Handles all delivery scheduling UI interactions on cart page
 * 
 * UPDATED: Uses debounced batch updates to prevent race conditions
 * when user rapidly selects date, time, and placement
 * 
 * UPDATED: Resolves supplier config key from SKU prefix (e.g., "swartz" from 
 * "swartz.mulch.dyed_black.triple_shredded") instead of variant option value
 */

// ==========================================
// STANDALONE EARLIEST-DATE HELPERS
// ==========================================
// Page-agnostic functions for computing the earliest delivery date for a
// given supplier config key. Used by the PDP ETA pill (where there is no
// cart yet) and any other non-cart context.
//
// The DeliveryScheduler class below is unchanged and continues to own the
// cart-page date picker (via its own getValidDatesForSupplier method).
// Keep the algorithm in this block in sync with that method if either changes.

// ==========================================
// FIREWOOD EXTRA LEAD TIME
// ==========================================
// Firewood gets FIREWOOD_EXTRA_LEAD_DAYS on top of the supplier's normal lead
// (lead_before_cutoff / lead_after_cutoff in the supplier_lead_time shop
// metafield). Counted in BUSINESS days, the same unit the schedule uses, so a
// Friday order skips the weekend exactly as the base lead does.
// Applies to any delivery group holding a firewood SKU
// ("<prefix>.firewood.<type>.<size>"). Firewood and mulch from the same
// supplier ship as one delivery, so the whole delivery waits.
// Also read by the PDP delivery pill (sections/main-product.liquid) so the
// product page never promises a date the cart will not offer.
window.FIREWOOD_EXTRA_LEAD_DAYS = 7;
window.isFirewoodSku = function (sku) { return /\.firewood\./i.test(sku || ''); };

(function () {
  function getNowInTimezone(timezone) {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).formatToParts(now);
    const get = (type) => parts.find(p => p.type === type)?.value;
    return new Date(
      parseInt(get('year')),
      parseInt(get('month')) - 1,
      parseInt(get('day')),
      parseInt(get('hour')),
      parseInt(get('minute'))
    );
  }

  function stripTime(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function parseTime(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  function formatDateISO(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Compute the earliest valid delivery date for a given supplier config key.
   * Returns a Date, or null if no valid date exists within max_advance_days.
   *
   * Requires window.getSupplierConfig to be loaded (from supplier-lead-time-loader snippet).
   * extraLeadDays (optional) adds business days on top of the schedule's lead,
   * used for firewood (see FIREWOOD_EXTRA_LEAD_DAYS).
   */
  function getEarliestDeliveryDate(configKey, extraLeadDays) {
    if (typeof window.getSupplierConfig !== 'function') return null;
    const config = window.getSupplierConfig(configKey);
    if (!config) return null;

    const now = getNowInTimezone(config.timezone);
    const today = stripTime(now);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + config.max_advance_days);

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    // Determine lead days from today's schedule + cutoff
    const todayDayName = dayNames[now.getDay()];
    const todaySchedule = config.schedule[todayDayName];
    let leadDays = 1;
    if (todaySchedule) {
      const cutoffTime = parseTime(todaySchedule.cutoff);
      const currentTime = now.getHours() * 60 + now.getMinutes();
      leadDays = currentTime < cutoffTime
        ? todaySchedule.lead_before_cutoff
        : todaySchedule.lead_after_cutoff;
    }

    if (extraLeadDays > 0) leadDays = (Number(leadDays) || 0) + extraLeadDays;

    // Advance leadDays BUSINESS days forward (enabled schedule + not blacked out)
    const earliestDate = new Date(today);
    let businessDaysAdvanced = 0;
    while (businessDaysAdvanced < leadDays) {
      earliestDate.setDate(earliestDate.getDate() + 1);
      const dayName = dayNames[earliestDate.getDay()];
      const schedule = config.schedule[dayName];
      const dateStr = formatDateISO(earliestDate);
      if (schedule && schedule.enabled && !config.blackout_dates.includes(dateStr)) {
        businessDaysAdvanced++;
      }
      if (earliestDate > maxDate) return null;
    }

    // Walk forward if landing day is disabled/blacked out
    const dayName = dayNames[earliestDate.getDay()];
    const schedule = config.schedule[dayName];
    const dateStr = formatDateISO(earliestDate);
    if (!schedule || !schedule.enabled || config.blackout_dates.includes(dateStr)) {
      const checkDate = new Date(earliestDate);
      while (checkDate <= maxDate) {
        checkDate.setDate(checkDate.getDate() + 1);
        const dn = dayNames[checkDate.getDay()];
        const sc = config.schedule[dn];
        const ds = formatDateISO(checkDate);
        if (sc && sc.enabled && !config.blackout_dates.includes(ds)) {
          return checkDate;
        }
      }
      return null;
    }

    return earliestDate;
  }

  window.getEarliestDeliveryDate = getEarliestDeliveryDate;
})();


class DeliveryScheduler {
  constructor() {
    this.cart = null;
    this.pendingUpdates = {}; // { supplierId: { property: value, ... } }
    this.debounceTimer = null;
    this.isFlushing = false;
    this.init();
  }

  async init() {
    await this.loadCart();
    this.bindAMPMToggles();
    this.bindPlacementDropdowns();
    this.bindSpecialInstructions();
    this.bindDatePickers();
    this.bindClickOutside();
    this.hideAMPMIfDisabled();
  }

  // ==========================================
  // Hide AM/PM if supplier has am_pm_enabled: false
  // ==========================================
  hideAMPMIfDisabled() {
    document.querySelectorAll('.delivery-group').forEach(group => {
      // Skip dropship groups (they don't have delivery scheduling fields)
      if (group.dataset.deliveryType === 'dropship') return;
      
      const supplier = group.dataset.supplier;
      const configKey = this.resolveSupplierConfigKey(supplier);
      const config = this.getSupplierConfig(configKey);
      
      if (config && config.am_pm_enabled === false) {
        const timeField = group.querySelector('.delivery-field__toggle-group[data-field="time-window"]');
        if (timeField) {
          timeField.closest('.delivery-field').style.display = 'none';
        }
      }
    });
  }

  async loadCart() {
    const response = await fetch('/cart.js');
    this.cart = await response.json();
  }

  // Get supplier from cart item (by option name)
  getSupplier(item) {
    if (!item.options_with_values) return 'default';
    const supplierOption = item.options_with_values.find(
      opt => opt.name.toLowerCase() === 'supplier'
    );
    return supplierOption?.value || 'default';
  }

  // Normalize supplier for metafield key lookup
  normalizeSupplier(value) {
    return value.toLowerCase().replace(/\s+/g, '_');
  }

  // ==========================================
  // Resolve supplier config key from SKU prefix
  // ==========================================
  /**
   * Given a supplier option value (e.g., "Dayton"), find the matching cart items
   * and extract the SKU prefix (e.g., "swartz" from "swartz.mulch.dyed_black.triple_shredded")
   * to use as the metafield config key.
   */
  resolveSupplierConfigKey(supplierOptionValue) {
    if (!this.cart || !this.cart.items) return supplierOptionValue;

    // Find a cart item belonging to this supplier group
    const item = this.cart.items.find(item => {
      if (!item.options_with_values) return false;
      const supplierOption = item.options_with_values.find(
        opt => opt.name.toLowerCase() === 'supplier'
      );
      return (supplierOption?.value || 'default') === supplierOptionValue;
    });

    if (item && item.sku) {
      const prefix = item.sku.split('.')[0].toLowerCase();
      console.log(`Resolved supplier config: "${supplierOptionValue}" → "${prefix}" (from SKU: ${item.sku})`);
      return prefix;
    }

    // Fallback to option value if no SKU found
    console.warn(`No SKU found for supplier "${supplierOptionValue}", falling back to option value`);
    return supplierOptionValue;
  }

  // True when any cart item in this supplier's delivery group is firewood.
  groupHasFirewood(supplierOptionValue, line) {
    if (!this.cart || !this.cart.items) return false;
    return this.cart.items.some(item =>
      this.getSupplier(item) === supplierOptionValue && (!line || String(item.variant_id) === String(line)) && window.isFirewoodSku(item.sku)
    );
  }

  // The product line a delivery group is for (one delivery per product line, Alec 2026-10-12): its variant id, from
  // the group's data-line; '' for a group that is not per line.
  lineOf(deliveryIndex) {
    const g = document.querySelector('.delivery-group[data-delivery-index="' + deliveryIndex + '"]');
    return (g && g.dataset.line) || '';
  }

  // Get supplier config (uses global function from Liquid)
  getSupplierConfig(supplier) {
    return window.getSupplierConfig ? window.getSupplierConfig(supplier) : null;
  }

  // ==========================================
  // DEBOUNCED BATCH UPDATE SYSTEM
  // ==========================================
  
  /**
   * Queue a property update for a supplier.
   * Updates are collected and flushed after 500ms of inactivity.
   */
  updateDeliveryProperty(supplierId, deliveryIndex, property, value) {
    // One delivery per product line: the choice is that line's only (key "supplier|variant")
    const line = this.lineOf(deliveryIndex);
    const key = line ? supplierId + '|' + line : supplierId;
    if (!this.pendingUpdates[key]) {
      this.pendingUpdates[key] = {};
    }
    this.pendingUpdates[key][property] = value;
    
    // Optimistic UI update - update local cart state immediately
    const itemsToUpdate = this.cart.items.filter(item => 
      this.getSupplier(item) === supplierId && (!line || String(item.variant_id) === line)
    );
    itemsToUpdate.forEach(item => {
      item.properties = item.properties || {};
      item.properties[property] = value;
    });

    console.log(`Queued ${property} = "${value}" for ${supplierId}`);

    // Debounce: wait 500ms after last change, then flush all
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.flushUpdates(), 500);
  }

  /**
   * Flush all pending updates to the cart API.
   * Processes each supplier sequentially, fetching fresh cart state before each update.
   */
  async flushUpdates() {
    // Prevent concurrent flushes
    if (this.isFlushing) {
      console.log('Flush already in progress, will retry...');
      this.debounceTimer = setTimeout(() => this.flushUpdates(), 200);
      return;
    }

    // Grab pending updates and clear
    const updates = { ...this.pendingUpdates };
    this.pendingUpdates = {};

    // Exit if nothing to flush
    if (Object.keys(updates).length === 0) {
      return;
    }

    this.isFlushing = true;
    console.log('Flushing updates:', updates);

    try {
      for (const [key, properties] of Object.entries(updates)) {
        const bar = key.lastIndexOf('|');
        const supplierId = bar >= 0 ? key.slice(0, bar) : key, line = bar >= 0 ? key.slice(bar + 1) : '';
        // Get fresh cart state before updating this supplier's items
        const freshCart = await fetch('/cart.js').then(r => r.json());
        
        // Find items for this supplier (and, per line, only that product's line)
        const items = freshCart.items.filter(item => {
          if (!item.options_with_values) return false;
          const supplierOption = item.options_with_values.find(
            opt => opt.name.toLowerCase() === 'supplier'
          );
          return (supplierOption?.value || 'default') === supplierId && (!line || String(item.variant_id) === line);
        });

        if (items.length === 0) {
          console.warn(`No items found for supplier: ${supplierId}`);
          continue;
        }

        // Update each item sequentially
        for (const item of items) {
          // Merge existing properties with new ones
          const mergedProperties = {
            ...item.properties,
            ...properties  // Apply ALL pending properties at once
          };

          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              properties: mergedProperties
            })
          });

          console.log(`Updated item ${item.key} with:`, mergedProperties);
        }
      }

      // Refresh local cart state after all updates
      await this.loadCart();
      console.log('All delivery updates flushed successfully');

    } catch (err) {
      console.error('Error flushing updates:', err);
      // Reload cart to get actual state
      await this.loadCart();
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Force-flush any pending/in-flight updates and resolve only once the cart
   * has them written. Called right before checkout so delivery selections
   * can't be lost to the 500ms debounce. Bounded (~5s worst case); never
   * rejects — a flush failure must not trap the customer at checkout.
   */
  async flushNow() {
    clearTimeout(this.debounceTimer);
    for (let i = 0; i < 100; i++) {
      if (this.isFlushing) { await new Promise(r => setTimeout(r, 50)); continue; }
      if (Object.keys(this.pendingUpdates).length === 0) break;
      try { await this.flushUpdates(); } catch (e) { console.error('flushNow:', e); break; }
    }
  }

  // ==========================================
  // AM/PM Toggle
  // ==========================================
  bindAMPMToggles() {
    document.querySelectorAll('.delivery-field__toggle-group[data-field="time-window"]').forEach(group => {
      const buttons = group.querySelectorAll('.delivery-field__toggle');
      const supplier = group.dataset.supplier;
      const deliveryIndex = group.dataset.deliveryIndex;

      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          // Remove active from siblings
          buttons.forEach(b => b.classList.remove('active'));
          // Add active to clicked
          btn.classList.add('active');
          
          // Clear validation error
          const field = group.closest('.delivery-field');
          if (field) field.classList.remove('has-error');
          
          const value = btn.dataset.value;
          this.updateDeliveryProperty(supplier, deliveryIndex, '_delivery_window', value);
        });
      });
    });
  }

  // ==========================================
  // Placement Dropdown
  // ==========================================
  bindPlacementDropdowns() {
    document.querySelectorAll('.delivery-field__placement-dropdown').forEach(dropdown => {
      const trigger = dropdown.querySelector('.delivery-field__placement-trigger');
      const options = dropdown.querySelectorAll('.delivery-field__placement-option');
      const textEl = dropdown.querySelector('.delivery-field__placement-text');
      const supplier = dropdown.dataset.supplier;
      const deliveryIndex = dropdown.dataset.deliveryIndex;

      // Toggle popover
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        // Close other open dropdowns
        document.querySelectorAll('.delivery-field__placement-dropdown.open').forEach(d => {
          if (d !== dropdown) d.classList.remove('open');
        });
        dropdown.classList.toggle('open');
      });

      // Select option
      options.forEach(option => {
        option.addEventListener('click', () => {
          const value = option.dataset.value;
          const label = option.querySelector('.delivery-field__placement-label').textContent;

          // Update active state
          options.forEach(o => o.classList.remove('active'));
          option.classList.add('active');

          // Update trigger text
          textEl.textContent = label;

          // Close popover
          dropdown.classList.remove('open');

          // Clear validation error
          const field = dropdown.closest('.delivery-field');
          if (field) field.classList.remove('has-error');

          // Save to line item properties
          this.updateDeliveryProperty(supplier, deliveryIndex, '_placement', value);
        });
      });
    });
  }

  // ==========================================
  // Special Instructions Collapse
  // ==========================================
  bindSpecialInstructions() {
    document.querySelectorAll('.delivery-field--collapsible').forEach(field => {
      const trigger = field.querySelector('.delivery-field__collapse-trigger');
      const textarea = field.querySelector('.delivery-field__textarea');
      const supplier = field.dataset.supplier;
      const deliveryIndex = field.dataset.deliveryIndex;

      trigger.addEventListener('click', () => {
        field.classList.toggle('open');
        if (field.classList.contains('open')) {
          textarea.focus();
        }
      });

      // Save on blur
      if (textarea) {
        textarea.addEventListener('blur', () => {
          const value = textarea.value.trim();
          this.updateDeliveryProperty(supplier, deliveryIndex, '_special_instructions', value);
        });
      }
    });
  }

  // ==========================================
  // Date Picker (Flatpickr)
  // ==========================================
  bindDatePickers() {
    document.querySelectorAll('.delivery-field__date-trigger').forEach(trigger => {
      const supplier = trigger.dataset.supplier;
      const deliveryIndex = trigger.dataset.deliveryIndex;
      const wrapper = trigger.closest('.delivery-field__date-trigger-wrapper');
      const hiddenInput = wrapper.querySelector('.delivery-date-picker');
      const textEl = trigger.querySelector('.delivery-field__date-text');

      // Resolve config key from SKU prefix instead of option value
      const configKey = this.resolveSupplierConfigKey(supplier);

      // Get valid dates using resolved config key. A delivery carrying firewood
      // waits FIREWOOD_EXTRA_LEAD_DAYS longer (see top of file).
      const extraLead = this.groupHasFirewood(supplier, trigger.dataset.line) ? (window.FIREWOOD_EXTRA_LEAD_DAYS || 0) : 0;
      const validDates = this.getValidDatesForSupplier(configKey, extraLead);

      // Initialize Flatpickr
      const fp = flatpickr(hiddenInput, {
        enable: validDates,
        dateFormat: 'Y-m-d',
        altInput: false,
        clickOpens: false,
        positionElement: trigger,
        onChange: (selectedDates, dateStr) => {
          if (selectedDates.length > 0) {
            // Format for display (e.g., "Dec 19")
            const displayDate = selectedDates[0].toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric' 
            });
            textEl.textContent = displayDate;

            // Clear validation error
            const field = trigger.closest('.delivery-field');
            if (field) field.classList.remove('has-error');

            // Save to line item properties (use original supplier option value for cart updates)
            this.updateDeliveryProperty(supplier, deliveryIndex, '_delivery_date', dateStr);
          }
        }
      });

      // Open on trigger click
      trigger.addEventListener('click', () => {
        fp.open();
      });
    });
  }

  // ==========================================
  // Click Outside Handler
  // ==========================================
  bindClickOutside() {
    document.addEventListener('click', (e) => {
      // Close placement dropdowns
      if (!e.target.closest('.delivery-field__placement-dropdown')) {
        document.querySelectorAll('.delivery-field__placement-dropdown.open').forEach(d => {
          d.classList.remove('open');
        });
      }
    });
  }

  // ==========================================
  // Get Valid Dates for Supplier
  // ==========================================
  getValidDatesForSupplier(supplierId, extraLeadDays = 0) {
    const config = this.getSupplierConfig(supplierId);
    if (!config) {
      console.warn(`No config found for supplier: ${supplierId}`);
      // Return next 14 days as fallback
      return this.getNextNDays(14);
    }

    const validDates = [];
    const now = this.getNowInTimezone(config.timezone);
    const today = this.stripTime(now);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + config.max_advance_days);

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    // Determine earliest delivery date
    const todayDayName = dayNames[now.getDay()];
    const todaySchedule = config.schedule[todayDayName];
    
    let leadDays = 1; // default
    if (todaySchedule) {
      const cutoffTime = this.parseTime(todaySchedule.cutoff);
      const currentTime = now.getHours() * 60 + now.getMinutes();
      
      if (currentTime < cutoffTime) {
        leadDays = todaySchedule.lead_before_cutoff;
      } else {
        leadDays = todaySchedule.lead_after_cutoff;
      }
    }

    if (extraLeadDays > 0) leadDays = (Number(leadDays) || 0) + extraLeadDays;

    // Advance leadDays BUSINESS days forward to find the earliest delivery date.
    // A "business day" = enabled in schedule AND not in blackout_dates.
    // Non-business days (disabled weekdays, weekends, blackouts) do NOT count
    // toward the lead — they're skipped. Previous logic used calendar days,
    // which caused weekend orders to under-count lead time (e.g. a Sat evening
    // order with lead_after=2 would land Monday instead of Tuesday).
    const earliestDate = new Date(today);
    let businessDaysAdvanced = 0;
    while (businessDaysAdvanced < leadDays) {
      earliestDate.setDate(earliestDate.getDate() + 1);

      const dayName = dayNames[earliestDate.getDay()];
      const schedule = config.schedule[dayName];
      const dateStr = this.formatDateISO(earliestDate);

      if (schedule && schedule.enabled && !config.blackout_dates.includes(dateStr)) {
        businessDaysAdvanced++;
      }

      // Safety: bail if we've walked past the max advance window
      // (e.g. supplier has no enabled days, or everything is blacked out)
      if (earliestDate > maxDate) break;
    }

    // Collect all valid (enabled, non-blackout) dates from earliestDate through maxDate
    const checkDate = new Date(earliestDate);
    while (checkDate <= maxDate) {
      const dayName = dayNames[checkDate.getDay()];
      const schedule = config.schedule[dayName];
      const dateStr = this.formatDateISO(checkDate);

      // Check if day is enabled and not blacked out
      if (schedule && schedule.enabled && !config.blackout_dates.includes(dateStr)) {
        validDates.push(new Date(checkDate));
      }

      checkDate.setDate(checkDate.getDate() + 1);
    }

    return validDates;
  }

  // ==========================================
  // Utility Functions
  // ==========================================
  
  getNowInTimezone(timezone) {
    // Get current time in specified timezone
    const now = new Date();
    const options = { timeZone: timezone, hour12: false };
    const parts = new Intl.DateTimeFormat('en-US', {
      ...options,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).formatToParts(now);

    const get = (type) => parts.find(p => p.type === type)?.value;
    
    return new Date(
      parseInt(get('year')),
      parseInt(get('month')) - 1,
      parseInt(get('day')),
      parseInt(get('hour')),
      parseInt(get('minute'))
    );
  }

  stripTime(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  parseTime(timeStr) {
    // Parse "14:00" to minutes since midnight
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  formatDateISO(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getNextNDays(n) {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= n; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  }

  formatDateForDisplay(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  formatPlacementLabel(value) {
    const labels = {
      'top_left': 'Top Left',
      'top_right': 'Top Right',
      'bottom_left': 'Bottom Left',
      'bottom_right': 'Bottom Right'
    };
    return labels[value] || 'Select';
  }
}

// ==========================================
// Initialize on DOM Ready
// ==========================================
document.addEventListener('DOMContentLoaded', () => {
  // Check if we're on cart page with delivery groups
  if (document.querySelector('.delivery-group')) {
    window.deliveryScheduler = new DeliveryScheduler();
  }
});

// ==========================================
// Re-initialize after AJAX cart updates
// ==========================================
if (typeof subscribe !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    // Small delay to let DOM update
    setTimeout(() => {
      if (document.querySelector('.delivery-group')) {
        window.deliveryScheduler = new DeliveryScheduler();
      }
    }, 100);
  });
}